import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useRecipeService, Recipe } from '../services/recipeService';
import axios from 'axios';
import {
  ChefHat,
  ArrowLeft,
  ArrowRight,
  Edit,
  Utensils,
  Clock,
  Book,
} from 'lucide-react';
import {
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import RecipeDetailsSection from './../components/RecipeDetails';
import styles from './../styles/RecipeGenerator.module.scss';
import { useAuth } from './../components/AuthContext';
import { useUserId } from './../components/UserIdHook';
import GradientButton from './../components/GradientButton';
import LoadingImage from '../assets/loading.gif';
import SignInForm from './../components/SignInPage';
import { useAppCache } from './../context/AppCacheContext';

interface Question {
  id: string;
  text: string;
  options: string[];
  condition?: (answers: Record<string, string>) => boolean;
}

enum Step {
  Questions,
  AdditionalInfo,
  Summary,
  Result,
}

const questions: Question[] = [
  {
    id: 'dietType',
    text: 'What type of diet are you following?',
    options: [
      'No restrictions',
      'Vegetarian',
      'Vegan',
      'Pescatarian',
      'Keto',
      'Paleo',
    ],
  },
  {
    id: 'meatPreference',
    text: 'What type of meat do you prefer?',
    options: ['Chicken', 'Beef', 'Pork', 'Lamb', 'Turkey', 'No preference'],
    condition: (answers) =>
      ['No restrictions', 'Keto', 'Paleo'].includes(answers.dietType),
  },
  {
    id: 'mealType',
    text: 'What type of meal are you looking for?',
    options: ['Breakfast', 'Lunch', 'Dinner', 'Snack'],
  },
  {
    id: 'cookingTime',
    text: 'How much time do you have for cooking?',
    options: [
      '15 minutes or less',
      '30 minutes',
      '45 minutes',
      '1 hour or more',
    ],
  },
  {
    id: 'skillLevel',
    text: "What's your cooking skill level?",
    options: ['Beginner', 'Intermediate', 'Advanced'],
  },
];

const cuisines = [
  'Italian',
  'Chinese',
  'Indian',
  'Mexican',
  'French',
  'Japanese',
  'Mediterranean',
  'Thai',
  'Spanish',
  'American',
  'Korean',
  'Vietnamese',
  'Turkish',
  'Greek',
  'Lebanese',
  'Caribbean',
  'Moroccan',
  'Brazilian',
  'Argentinian',
  'Ethiopian',
  'Persian',
  'Russian',
  'Cuban',
  'German',
  'Filipino',
  'Indonesian',
  'Pakistani',
  'Sri Lankan',
  'Malaysian',
  'Afghan',
  'Portuguese',
  'Australian',
  'Polish',
  'Scandinavian',
  'Irish',
  'Hungarian',
  'Peruvian',
  'Egyptian',
  'South African',
  'Jamaican',
  'Tibetan',
  'Nepalese',
  'Other',
];

const RecipeGenerator: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<Step>(Step.Questions);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [cuisine, setCuisine] = useState<string>('');
  const [description, setDescription] = useState('Surprise me with your recipe! Keep it concise and engaging.');
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const recipeDetailsRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const user_id = useUserId(user) ?? '';
  const navigate = useNavigate();
  const [isSignInVisible, setIsSignInVisible] = useState<boolean>(false); // To toggle sign-in form
  const [pendingSave, setPendingSave] = useState<boolean>(false); // Flag to trigger save after login
  const { setCache } = useAppCache();
  const { fetchRecipes } = useRecipeService();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>([]);
  const [error, setError] = useState<string | null>(null);

  
  const loadRecipes = useCallback(async () => {
        setIsLoading(true);
        try {
            const { recipes: fetchedRecipes, fromCache } = await fetchRecipes();
            setRecipes(fetchedRecipes);
            setFilteredRecipes(fetchedRecipes);
            if (!fromCache) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        } catch (error) {
            console.error('Error loading recipes:', error);
            setError('Failed to load recipes. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [fetchRecipes]);

  useEffect(() => {
    if (user_id && recipe && showSignIn) {
      // Auto-save the recipe after login
      handleSaveRecipe(recipe);
      setShowSignIn(false); // Hide sign-in form after login
    }
  }, [user_id, recipe, showSignIn]);

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleCuisineChange = (event: SelectChangeEvent) => {
    setCuisine(event.target.value as string);
  };

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setDescription(event.target.value.slice(0, 500));
  };

  const handleAdditionalPromptChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAdditionalPrompt(event.target.value.slice(0, 200));
  };

  const handleLoginSuccess = () => {
    setIsSignInVisible(false); // Hide the sign-in form after successful login
    if (pendingSave) {
      setPendingSave(false);
      handleSaveRecipe(recipe!); // Save the recipe after login
    }
  };

  const getNextQuestionIndex = (currentIndex: number): number => {
    let nextIndex = currentIndex + 1;
    while (nextIndex < questions.length) {
      const question = questions[nextIndex];
      if (!question.condition || question.condition(answers)) {
        return nextIndex;
      }
      nextIndex++;
    }
    return questions.length;
  };

  const handleNext = () => {
    if (currentStep === Step.Questions) {
      const nextQuestionIndex = getNextQuestionIndex(currentQuestionIndex);
      if (nextQuestionIndex < questions.length) {
        setCurrentQuestionIndex(nextQuestionIndex);
      } else {
        setCurrentStep(Step.AdditionalInfo);
      }
    } else if (currentStep === Step.AdditionalInfo) {
      if (cuisine) {
        setCurrentStep(Step.Summary);
        setErrorMessage(null);
      } else {
        setErrorMessage('Please select a cuisine before proceeding.');
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep === Step.AdditionalInfo) {
      setCurrentStep(Step.Questions);
      setCurrentQuestionIndex(questions.length - 1);
    } else if (currentStep === Step.Summary) {
      setCurrentStep(Step.AdditionalInfo);
    } else if (currentStep === Step.Questions && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleEdit = (step: Step) => {
    setCurrentStep(step);
    if (step === Step.Questions) {
      setCurrentQuestionIndex(0);
    }
  };

  const handleCreateRecipe = async () => {
    setIsLoading(true);
    try {
      const prompt = `
        Diet Type: ${answers.dietType}
        Meat Preference: ${answers.meatPreference || 'N/A'}
        Meal Type: ${answers.mealType}
        Cooking Time: ${answers.cookingTime}
        Skill Level: ${answers.skillLevel}
        Cuisine: ${cuisine}
        Description: ${description}
        Additional Prompt: ${additionalPrompt || 'N/A'}
      `;

      const requestBody = {
        prompt,
        content_type: 'json',
        bucket_name: 'privatebot-base',
        file_name: 'recipe.json',
      };

      const response = await axios.post(
        'https://1vllqk52u3.execute-api.us-east-1.amazonaws.com/v1/generate',
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const parsedResponse = JSON.parse(response.data.body);
      let contentUrl = parsedResponse.content_url;
      contentUrl = contentUrl.trim().replace(/```/g, '').replace(/\\n/g, '').replace(/\\"/g, '"');
      const recipeData = JSON.parse(contentUrl);
      setRecipe(recipeData);
      setCurrentStep(Step.Result);

      if (recipeDetailsRef.current) {
        recipeDetailsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error creating recipe:', error);
      alert('There was an error generating the recipe. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRecipe = async (updatedData: Recipe): Promise<void> => {
    if (!recipe || !user_id) {
      console.error("Error: Recipe or user ID is missing.");
      setIsSignInVisible(true); // Show the sign-in form
      setPendingSave(true);     // Mark that a save is pending
      return;
      
    }
    setIsSaving(true);
    const newId = recipe.id || new Date().toISOString().replace(/[-:.TZ]/g, '') + Math.floor(Math.random() * 1000);
    const createdTime = new Date().toISOString();

    const requestBody = {
      entity: 'RecipeTable',
      action: 'upsert',
      pk_column: 'recipe_id',
      pk_value: newId.toString(),
      columns: {
        recipe_name: updatedData.recipe_name || '',
        description: updatedData.description || 'Surprise me with your recipe! Keep it concise and engaging.',
        ingredients: JSON.stringify(updatedData.ingredients),
        instructions: JSON.stringify(updatedData.instructions),
        tried: updatedData.tried ? 'true' : 'false',
        average_rating: updatedData.average_rating?.toString() || '0',
        rating_count: updatedData.rating_count?.toString() || '0',
        meal_type: answers.mealType || '',
        diet_type: answers.dietType || '',
        cuisine: cuisine || '',
        prep_time: updatedData.prep_time || 0,
        total_time: updatedData.total_time || 0,
        cook_time: answers.cookingTime || 0,
        servings: updatedData.servings || 0,
        nutrition_info: updatedData.nutrition_info || '',
        createdBy: user_id,
        createdTime: createdTime,
      },
    };

    try {
      const response = await axios.post(
        'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields',
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const { statusCode, body } = response.data;

      if (statusCode === 200) {
        // Invalidate relevant cache entries
        setCache('allRecipes', null);
        setCache(`recipe_${newId}`, null);
        
        // If you have user-specific recipe caches, clear those too
        setCache(`userRecipes_${user_id}`, null);
        loadRecipes();
        navigate(`/recipe/${newId}`);
      } else {
        const errorMessage = JSON.parse(body).message;
        setErrorMessage('Something went wrong, please try again.');
      }
    } catch (error) {
      alert('Error saving recipe. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderQuestions = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const isAnswered = !!answers[currentQuestion.id];

    return (
      <div className={styles.questionContainer}>
        {errorMessage && (
          <div style={{
            backgroundColor: 'red',
            color: 'white',
            borderRadius: '20px',
            padding: '10px',
            textAlign: 'center',
            marginBottom: '20px',
          }}>
            {errorMessage}
          </div>
        )}
        <FormControl component="fieldset">
          <FormLabel component="legend">{currentQuestion.text}</FormLabel>
          <RadioGroup
            value={answers[currentQuestion.id] || ''}
            onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
          >
            {currentQuestion.options.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <div className={styles.navigationButtons}>
          {currentQuestionIndex > 0 && (
            <Button onClick={handlePrevious} startIcon={<ArrowLeft />}>
              Previous
            </Button>
          )}
          <Button
            onClick={handleNext}
            endIcon={<ArrowRight />}
            disabled={!isAnswered}
          >
            {currentQuestionIndex === questions.length - 1 ? 'Next' : 'Next'}
          </Button>
        </div>
      </div>
    );
  };

  const renderAdditionalInfo = () => (
    <div className={styles.additionalInfoContainer}>
      <FormControl fullWidth className={styles.cuisineSelect}>
        <InputLabel>Cuisine</InputLabel>
        <Select value={cuisine} onChange={handleCuisineChange}>
          {cuisines.map((cuisineOption) => (
            <MenuItem key={cuisineOption} value={cuisineOption}>
              {cuisineOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <InputLabel style={{
        color: '#8A2BE2', fontWeight: 'bold', fontSize: '16px',
        backgroundColor: '#F0F8FF', padding: '10px', borderRadius: '8px',
        display: 'block', textAlign: 'center',
      }}>
        Enter your recipe name or description, Ingredients to include, exclude etc., otherwise our engine will surprise you!!!
      </InputLabel>

      <TextField
        label="Recipe Description"
        multiline
        rows={4}
        value={description }
        onChange={handleDescriptionChange}
        variant="outlined"
        fullWidth
        margin="normal"
        placeholder="Surprise me with your recipe! Keep it concise and engaging."
      />

      <div className={styles.navigationButtons}>
        <Button onClick={handlePrevious} startIcon={<ArrowLeft />}>
          Previous
        </Button>
        <Button onClick={handleNext} endIcon={<ArrowRight />} disabled={!cuisine}>
          Next
        </Button>
      </div>
    </div>
  );

  const renderSummary = () => (
    <div className={styles.summaryContainer}>
      <h2>Your Recipe Preferences</h2>
      <ul>
        {questions.map((question) => (
          <li key={question.id}>
            <strong>{question.text}:</strong> {answers[question.id]}
          </li>
        ))}
        <li>
          <strong>Cuisine:</strong> {cuisine}
        </li>
        <li>
          <strong>Description:</strong> {description}
        </li>
        {additionalPrompt && (
          <li>
            <strong>Additional Prompt:</strong> {additionalPrompt}
          </li>
        )}
      </ul>
      <div className={styles.navigationButtons}>
        <Button onClick={() => handleEdit(Step.AdditionalInfo)} startIcon={<Edit />}>
          Edit
        </Button>
        <br />
        <GradientButton
          onClick={handleCreateRecipe}
          disabled={isLoading}
          style={{
            backgroundColor: isLoading ? '#f0ad4e' : '#007bff',
            color: 'white', padding: '10px 20px', fontWeight: 'bold', fontSize: '16px',
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} style={{ marginRight: '10px', color: 'white' }} />
              Generating Recipe...
            </>
          ) : 'Generate Recipe'}
        </GradientButton>
      </div>
    </div>
  );

  const renderResult = () => (
    <div className={styles.resultContainer}>
      {recipe ? (
        <>
        {isSignInVisible ? (
          <SignInForm onSuccess={handleLoginSuccess} /> // Show the sign-in form if needed
        ) : (
          <>
            {/* Render the recipe generation or result based on the current step */}
            {currentStep === Step.Result && (

          <GradientButton
            onClick={() => handleSaveRecipe(recipe)}
            disabled={isSaving}
            style={{
              backgroundColor: isSaving ? '#f0ad4e' : '#007bff',
              color: 'white', padding: '10px 20px', fontWeight: 'bold', fontSize: '16px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} style={{ marginRight: '10px', color: 'white' }} />
                Saving Recipe...
              </>
            ) : 'Save Recipe'}
          </GradientButton>
          )}
          </>
        )}


          <RecipeDetailsSection recipe={recipe} isEditing={false} onIngredientChange={() => { }} onInstructionChange={() => { }} onSaveRecipe={async () => Promise.resolve()} onEdit={() => { }} />
        </>
      ) : (
        <p>Failed to generate recipe. Please try again.</p>
      )}
    </div>
  );

  return (
    <div className={styles.pageContainer}>
      <div className={styles.mainSection}>
     

        <div className={styles.descriptionContainer}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={styles.heroContent}
          >
            <ChefHat size={64} className={styles.chefHat} />
            <header className={styles.header}>
              <h1 className={styles.mainTitle}>Your Personal Recipe Assistant</h1>
              <p className={styles.mainDescription}>
                Answer a few questions and let our AI dietician create the perfect recipe for you!
              </p>
            </header>
          </motion.div>
        </div>
        <div className={styles.recipeGeneratorContainer}>
          {showSignIn ? (
            <div className={styles.signInContainer}>
              <h2>Please sign in to save your recipe</h2>
              <Button onClick={() => navigate('/login')}>Sign In</Button>
            </div>
          ) : (
            <>
              {currentStep === Step.Questions && renderQuestions()}
              {currentStep === Step.AdditionalInfo && renderAdditionalInfo()}
              {currentStep === Step.Summary && renderSummary()}
              {currentStep === Step.Result && renderResult()}
            </>
          )}
        </div>
      </div>
      {isLoading && (
        <div className={styles.loadingContainer}>
          <img src={LoadingImage} alt="Loading..." className={styles.loadingImage} />
          <p>Creating your recipe... Please wait.</p>
        </div>
      )}
      <section className={styles.tipsSection}>
        <h2 className={styles.tipsTitle}>Cooking Tips</h2>
        <div className={styles.tipsGrid}>
          <div className={styles.tipCard}>
            <Utensils className={styles.tipIcon} />
            <h3>Knife Skills</h3>
            <p>Master basic knife techniques to improve your cooking efficiency and presentation.</p>
          </div>
          <div className={styles.tipCard}>
            <Clock className={styles.tipIcon} />
            <h3>Meal Prep</h3>
            <p>Save time by prepping ingredients in advance for quick and easy weeknight meals.</p>
          </div>
          <div className={styles.tipCard}>
            <Book className={styles.tipIcon} />
            <h3>Flavor Pairing</h3>
            <p>Learn which flavors complement each other to create more balanced and delicious dishes.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RecipeGenerator;
